<template>
  <div class="payment-page-container">
    <!-- Colonne principale -->
    <div class="main-column">
      <h1>COMMANDER</h1>

      <!-- Message succès -->
      <div v-if="showSuccess" class="alert success">
        Commande confirmée ! Votre panier a été vidé.
      </div>

      <!-- Mode de paiement -->
      <div class="section">
        <h2>Mode de Paiement</h2>
        <label>
          <input type="radio" name="payment" value="Paiement à la livraison" v-model="paymentMethod" />
          Paiement à la livraison
        </label>
        <label>
          <input type="radio" name="payment" value="Creditcard" v-model="paymentMethod" />
          Creditcard
        </label>
      </div>

      <!-- Section Creditcard -->
      <div v-if="paymentMethod === 'Creditcard'" class="creditcard-section">
        <p><strong>Mes adresses de facturation et d’expédition sont les mêmes.</strong></p>
        <div class="address-preview" v-if="selectedAddress">
          <p>{{ selectedAddress.company }}</p>
          <p>{{ selectedAddress.prefix }} {{ selectedAddress.firstName }} {{ selectedAddress.lastName }}</p>
          <p>{{ selectedAddress.address }}</p>
          <p>{{ selectedAddress.city }}, {{ selectedAddress.postalCode }}</p>
          <p>{{ selectedAddress.country }}</p>
          <p>{{ selectedAddress.phoneNumber }}</p>
        </div>

        <label>Type de carte de crédit</label>
        <select v-model="cardType">
          <option disabled value="">-- Faire un choix --</option>
          <option>Visa</option>
          <option>MasterCard</option>
        </select>

        <label>Nom du titulaire *</label>
        <input v-model="cardHolder" type="text" />
        <p v-if="errors.cardHolder" class="error">{{ errors.cardHolder }}</p>

        <label>Numéro de carte *</label>
        <input v-model="cardNumber" type="text" />
        <p v-if="errors.cardNumber" class="error">{{ errors.cardNumber }}</p>

        <label>Date d’expiration *</label>
        <div class="date-fields">
          <select v-model="expiryMonth">
            <option disabled value="">MM</option>
            <option v-for="n in 12" :key="n" :value="n">{{ n.toString().padStart(2, '0') }}</option>
          </select>
          <select v-model="expiryYear">
            <option disabled value="">AAAA</option>
            <option v-for="n in 10" :key="n" :value="new Date().getFullYear() + n - 1">
              {{ new Date().getFullYear() + n - 1 }}
            </option>
          </select>
        </div>
        <p v-if="errors.expiryDate" class="error">{{ errors.expiryDate }}</p>

        <label>CVV *</label>
        <input v-model="cvv" type="text" />
        <p v-if="errors.cvv" class="error">{{ errors.cvv }}</p>

        <label class="accept-terms">
          <input type="checkbox" v-model="acceptedTerms" />
          J'accepte les Conditions de Vente et j'autorise la transaction sur ma carte bancaire.
        </label>
        <p v-if="errors.terms" class="error">{{ errors.terms }}</p>
      </div>

      <!-- Paiement à la livraison -->
      <div v-if="paymentMethod === 'Paiement à la livraison'" class="delivery-conditions">
        <p><strong>Mes adresses de facturation et d’expédition sont les mêmes.</strong></p>

        <div class="address-preview" v-if="selectedAddress">
          <p>{{ selectedAddress.company }}</p>
          <p>{{ selectedAddress.prefix }} {{ selectedAddress.firstName }} {{ selectedAddress.lastName }}</p>
          <p>{{ selectedAddress.address }}</p>
          <p>{{ selectedAddress.city }}, {{ selectedAddress.postalCode }}</p>
          <p>{{ selectedAddress.country }}</p>
          <p>{{ selectedAddress.phoneNumber }}</p>
        </div>

        <label class="accept-terms">
          <input type="checkbox" v-model="acceptedTerms" />
          Je l'ai lu et accepté les Conditions de Vente.
        </label>
      </div>

      <div class="actions">
        <button @click="goBack" class="back-btn">Retour</button>
        <button @click="confirmPayment" class="confirm-btn" :disabled="loading">
          {{ loading ? 'Traitement...' : 'Confirmer' }}
        </button>
      </div>
    </div>

    <!-- Colonne résumé -->
    <div class="summary-column">
      <h2>RÉSUMÉ DE LA COMMANDE</h2>

      <!-- Toggle items -->
      <button @click="toggleItemsDropdown" class="dropdown-toggle">
        {{ getTotalQuantity }} Articles dans le panier
        <i :class="showItems ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </button>

      <!-- Liste des articles -->
      <div v-if="showItems && items && items.length" class="summary-content">
        <div v-for="(item, idx) in items" :key="idx" class="item">
          <img :src="item.image" alt="image produit" class="item-img" />
          <div class="item-details">
            <p>{{ item.name }} (Qté: {{ item.quantity }})</p>
            <p>{{ (item.price * item.quantity).toFixed(2) }} MAD</p>
          </div>
        </div>
      </div>

      <!-- Totaux -->
      <div class="total">
        <p>Sous-total panier : {{ subTotal.toFixed(2) }} MAD</p>
        <p>Livraison : Gratuit</p>
        <p><strong>Total de la commande TTC : {{ subTotal.toFixed(2) }} MAD</strong></p>
      </div>

      <!-- Adresse expédition -->
      <div class="section">
        <h2>Expédié à :</h2>
        <div v-if="selectedAddress" class="shipping-info">
          <p>{{ selectedAddress.company }}</p>
          <p>{{ selectedAddress.prefix }} {{ selectedAddress.firstName }} {{ selectedAddress.lastName }}</p>
          <p>{{ selectedAddress.address }}</p>
          <p>{{ selectedAddress.city }}, {{ selectedAddress.postalCode }}</p>
          <p>{{ selectedAddress.country }}</p>
          <p>{{ selectedAddress.phoneNumber }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from "@/Store";

export default {
  name: "PaymentPage",
  data() {
    return {
      items: [],
      selectedAddress: null,
      selectedShipping: null,
      paymentMethod: "",
      subTotal: 0,
      showItems: false,
      acceptedTerms: false,

      // CB
      cardType: "",
      cardHolder: "",
      cardNumber: "",
      expiryMonth: "",
      expiryYear: "",
      cvv: "",
      errors: {},

      // UI
      loading: false,
      showSuccess: false,
    };
  },

  created() {
    this.loadSelectedData();
  },

  computed: {
    getTotalQuantity() {
      return this.items.reduce((acc, item) => acc + item.quantity, 0);
    },
  },

  methods: {
    // Chargement des données cohérent avec le flux: items => sessionStorage('cart')
    loadSelectedData() {
      const addresses = JSON.parse(localStorage.getItem("addresses") || "[]");
      const selectedAddressId = parseInt(localStorage.getItem("selectedAddressId"), 10);
      const selectedShipping = localStorage.getItem("selectedShipping");

      //  Panier depuis la session (rempli depuis Panier)
      const items = JSON.parse(sessionStorage.getItem("cart") || "[]");

      if (addresses.length && selectedAddressId) {
        this.selectedAddress = addresses.find((a) => a.id === selectedAddressId) || null;
      }
      if (selectedShipping) this.selectedShipping = selectedShipping;

      this.items = items;
      this.subTotal = this.items.reduce((t, it) => t + it.price * it.quantity, 0);
    },

    toggleItemsDropdown() {
      this.showItems = !this.showItems;
    },

    goBack() {
      this.$router.push("/shipping");
    },

    // Validations simples CB
    isValidCardNumber(number) {
      return /^[0-9]{13,19}$/.test(number);
    },
    isValidCVV(cvv) {
      return /^[0-9]{3,4}$/.test(cvv);
    },
    isExpirationValid(month, year) {
      const now = new Date();
      const selected = new Date(year, month - 1);
      return selected >= new Date(now.getFullYear(), now.getMonth());
    },

    async confirmPayment() {
      // Réinitialiser erreurs
      this.errors = {};

      if (!this.paymentMethod) {
        alert("Veuillez sélectionner un mode de paiement.");
        return;
      }

      // Validations selon le mode
      if (this.paymentMethod === "Creditcard") {
        if (!this.cardHolder) this.errors.cardHolder = "Le nom du titulaire est requis.";
        if (!this.cardNumber || !this.isValidCardNumber(this.cardNumber)) {
          this.errors.cardNumber = "Numéro de carte invalide (13 à 19 chiffres).";
        }
        if (!this.isExpirationValid(this.expiryMonth, this.expiryYear)) {
          this.errors.expiryDate = "Date d'expiration invalide.";
        }
        if (!this.isValidCVV(this.cvv)) {
          this.errors.cvv = "CVV invalide.";
        }
        if (!this.acceptedTerms) {
          this.errors.terms = "Veuillez accepter les conditions.";
        }
      } else if (this.paymentMethod === "Paiement à la livraison") {
        if (!this.acceptedTerms) {
          this.errors.terms = "Veuillez accepter les conditions.";
        }
      }

      if (Object.keys(this.errors).length) return;

      if (!this.items.length) {
        alert("Votre panier est vide.");
        return;
      }
      if (!this.selectedAddress) {
        alert("Adresse de livraison manquante.");
        return;
      }

      this.loading = true;

      try {
        // Infos utilisateur
        const token = sessionStorage.getItem("token");
        const userStr = sessionStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
        if (!user) throw new Error("Utilisateur non connecté.");

        // (Optionnel) Email de confirmation
        try {
          await fetch("http://localhost:2028/api/sendConfirmation", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              user,
              selectedAddress: this.selectedAddress,
              items: this.items,
              total: this.subTotal,
            }),
          });
        } catch (e) {
          // On loggue mais on ne bloque pas la commande sur l'email
          console.warn("sendConfirmation a échoué :", e);
        }

        // (Optionnel) Création commande
        const orderRes = await fetch("http://localhost:2028/api/orders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            userId: user._id,
            items: this.items,
            total: this.subTotal,
            selectedAddress: this.selectedAddress,
            paymentMethod: this.paymentMethod,
          }),
        });

        if (!orderRes.ok) {
          const err = await orderRes.json().catch(() => ({}));
          throw new Error(err.message || "Création de commande échouée.");
        }

        // Vider le panier PARTOUT
        if (store?.clearCart) {
          store.clearCart();
        } else if (Array.isArray(store?.cart)) {
          store.cart.splice(0);
        }
        sessionStorage.setItem("cart", JSON.stringify([]));
        // Nettoyage optionnel d'anciens usages
        localStorage.removeItem("items");
        sessionStorage.removeItem("items");

        // UI: message succès + vider l'affichage
        this.items = [];
        this.subTotal = 0;
        this.showItems = false;
        this.showSuccess = true;

        // Redirection douce après 1.2s
        setTimeout(() => {
          this.$router.replace("/send-confirmation");
        }, 1200);
      } catch (e) {
        console.error(e);
        alert("Erreur lors du paiement : " + e.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ====== MODE CLAIR PAR DÉFAUT ====== */
.payment-page-container {
  display: flex;
  justify-content: space-between;
  padding: 20px;
  background-color: #f4f4f4;
}
.main-column {
  width: 65%;
  background: white;
  padding: 20px;
  border-radius: 12px;
}
.summary-column {
  width: 30%;
  background: white;
  padding: 20px;
  border-radius: 12px;
}
.section { margin-bottom: 20px; }

.delivery-conditions {
  margin-top: 15px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
}
.accept-terms { display: flex; align-items: center; margin-top: 15px; }
.accept-terms input { margin-right: 10px; }

.address-preview {
  margin-top: 10px;
  padding-left: 10px;
  border-left: 4px solid #28a745;
}

.item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}
.item-img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 12px;
}
.item-details {
  flex: 1;
  display: flex;
  justify-content: space-between;
}
.total {
  border-top: 1px solid #ddd;
  padding-top: 10px;
}

.back-btn, .confirm-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 20px;
}
.back-btn { background-color: #6c757d; color: white; }
.confirm-btn { background-color: #28a745; color: white; margin-left: 10px; }

.dropdown-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  padding: 10px 0;
}
.dropdown-toggle i {
  margin-left: 8px;
  transition: transform 0.2s ease;
}

.error {
  color: red;
  font-size: 0.9rem;
  margin-bottom: 10px;
}
.date-fields {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.creditcard-section {
  margin-top: 20px;
  background-color: #f8f8f8;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #ccc;
}
.creditcard-section label {
  font-weight: 500;
  display: block;
  margin-bottom: 6px;
}
.creditcard-section input,
.creditcard-section select {
  width: 40%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.alert.success {
  background: #e8f8ef;
  border: 1px solid #b8ebcb;
  color: #1e7e34;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* 📱 ====== RESPONSIVE ====== */
@media (max-width: 1024px) {
  .payment-page-container {
    flex-direction: column;
    padding: 15px;
  }
  .main-column, .summary-column {
    width: 100% !important;
    margin-bottom: 20px;
    padding: 20px;
  }
  .confirm-btn, .back-btn {
    width: 100%;
    font-size: 16px;
  }
  .creditcard-section input,
  .creditcard-section select {
    width: 100%;
  }
}

@media (max-width: 450px) {
  .payment-page-container { padding: 10px; }
  .main-column, .summary-column { padding: 15px; }
  .item { flex-direction: column; align-items: flex-start; }
  .item-img { margin-bottom: 10px; }
  .item-details { flex-direction: column; align-items: flex-start; gap: 5px; }
  .back-btn, .confirm-btn { width: 100%; font-size: 14px; padding: 12px; }
  .creditcard-section { padding: 15px; }
  .creditcard-section input,
  .creditcard-section select {
    width: 100%;
    font-size: 13px;
  }
}

/* ====== MODE SOMBRE AUTOMATIQUE ====== */
@media (prefers-color-scheme: dark) {
  .payment-page-container {
    background-color: #121212;
  }
  .main-column,
  .summary-column {
    background-color: #1e1e1e;
    color: #f1f1f1;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.05);
  }
  .delivery-conditions {
    background-color: #1f1f1f;
    border-color: #333;
    color: #ddd;
  }
  .address-preview {
    border-left-color: #28a745;
  }
  .item {
    border-bottom: 1px solid #333;
  }
  .total {
    border-top: 1px solid #333;
  }

  .back-btn {
    background-color: #444;
    color: #fff;
  }
  .back-btn:hover {
    background-color: #5c5c5c;
  }
  .confirm-btn {
    background-color: #078679;
    color: #fff;
  }
  .confirm-btn:hover {
    background-color: #21a396;
  }

  .creditcard-section {
    background-color: #222;
    border-color: #333;
  }
  .creditcard-section label {
    color: #eee;
  }
  .creditcard-section input,
  .creditcard-section select {
    background-color: #2a2a2a;
    border-color: #444;
    color: #fff;
  }
  .creditcard-section input::placeholder {
    color: #bbb;
  }

  .dropdown-toggle {
    color: #fff;
  }
  .error {
    color: #ff6b6b;
  }

  .alert.success {
    background: #1f3f2b;
    border-color: #2b6f47;
    color: #76d09a;
  }
}
</style>

